import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import {
  Button,
  FormGroup,
  FormFeedback,
  InputGroup,
  Label,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { showModal } from '../../actions';

import CollectionSelectModal from '../CollectionSelectModal';

import { userCan } from '../../helpers/Auth';

class UploadFileCollection extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      collectionValue: [],
      collectionSelectOpen: false,
    };

    this.handleNewCollection = this.handleNewCollection.bind(this);
    this.openCollectionSelectModal = this.openCollectionSelectModal.bind(this);
    this.closeCollectionSelectModal = this.closeCollectionSelectModal.bind(this);
  }

  componentDidMount() {
    this.getCollectionValue();
    this.setInitialCollection();
  }

  componentDidUpdate(prevProps) {
    const {
      collection,
      selectedFiles,
    } = this.props;
    const { collection: prevCollection } = prevProps;

    if (selectedFiles.length !== prevProps.selectedFiles.length) {
      this.getCollectionValue();
    }

    if (collection && collection.uuid !== prevCollection?.uuid) {
      this.setInitialCollection();
    }
  }

  setInitialCollection() {
    const {
      collectionUuid,
      collection,
      modalMode,
    } = this.props;

    if (modalMode !== 'edit' && collectionUuid && collection && !collection.smart) {
      this.handleNewCollection(collection);
    }
  }

  getCollectionValue() {
    const {
      modalMode,
      selectedFiles,
      isLinkEdit,
    } = this.props;

    if (modalMode === 'edit' || isLinkEdit) {
      return;
    }

    const values = [];
    selectedFiles.forEach((file) => {
      file.collections.forEach((collection) => {
        const vindex = values.findIndex(v => (v.uuid === collection.uuid));
        if (vindex >= 0) {
          values[vindex].count += 1;
        } else {
          values.push({
            uuid: collection.uuid,
            name: collection.name,
            spreadPermissions: collection.spreadPermissions,
            accessIcon: collection.accessIcon,
            accessDescription: collection.accessDescription,
            count: 1,
          });
        }
      });
    });

    this.setState({ collectionValue: values });
  }

  handleNewCollection(collection) {
    const { handleCollectionChange } = this.props;

    if (userCan('collection.add', collection)) {
      handleCollectionChange({
        uuid: collection.uuid,
        name: collection.name,
        spreadPermissions: !!(collection.spread_permissions &&
          collection.spread_permissions_always),
        accessIcon: collection.access_icon,
        accessDescription: collection.access_description,
      });
      this.getCollectionValue();
    }
  }

  handleRemoveSelectedCollection(collectionUuid) {
    const { handleCollectionChange } = this.props;
    const { collectionValue } = this.state;

    this.setState(
      { collectionValue: collectionValue.filter(c => (c.uuid !== collectionUuid)) },
      () => {
        const { collectionValue: newCollectionValue } = this.state;
        handleCollectionChange(newCollectionValue);
      },
    );
  }

  openCollectionSelectModal() {
    this.setState({ collectionSelectOpen: true });
  }

  closeCollectionSelectModal() {
    this.setState({ collectionSelectOpen: false });
  }

  render() {
    const {
      intl,
      collectionError,
      existingFiles,
      allowCollectionSelect,
    } = this.props;
    const {
      collectionValue,
      collectionSelectOpen,
    } = this.state;

    return (
      <FormGroup>
        <Label for="selectCollection">
          {intl.formatMessage({ defaultMessage: 'Add to an existing Collection' })}
        </Label>
        {(existingFiles && Object.keys(existingFiles).length > 0) && (
          <p>
            {intl.formatMessage(
              { defaultMessage: '{count} {count, plural, one {file} other {files}} will be saved as new version' },
              { count: Object.keys(existingFiles).length },
            )}
          </p>
        )}
        <InputGroup className={classnames('custom-react-select', { 'is-invalid': collectionError })}>
          {collectionValue.length > 0 && (
            <div className="tags-list">
              {collectionValue.map((c, idx) => (
                <div key={c.uuid} className="tag">
                  <span className="tag-label">
                    {c.name}
                  </span>
                  {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                  <span
                    className="tag-remove"
                    title={intl.formatMessage({ defaultMessage: 'Remove' })}
                    role="button"
                    tabIndex={idx}
                    onClick={() => {
                      this.handleRemoveSelectedCollection(c.uuid);
                    }}
                  >
                    <i className="far fa-times" />
                  </span>
                  {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                </div>
              ))}
            </div>
          )}
          {allowCollectionSelect && (
            <Button
              className="add-collection"
              color="secondary"
              onClick={this.openCollectionSelectModal}
            >
              {userCan('collection.create') ? (
                intl.formatMessage({ defaultMessage: 'Select or create Collection' })
              ) : (
                intl.formatMessage({ defaultMessage: 'Select Collection' })
              )}
            </Button>
          )}
        </InputGroup>
        {collectionError && (
          <FormFeedback style={{ display: 'block' }}>
            {userCan('collection.create') ? (
              intl.formatMessage({ defaultMessage: 'You must select or create a Collection' })
            ) : (
              intl.formatMessage({ defaultMessage: 'You must select a Collection' })
            )}
          </FormFeedback>
        )}
        <CollectionSelectModal
          modalOpen={collectionSelectOpen}
          enableAddNew={userCan('collection.create')}
          handleClose={this.closeCollectionSelectModal}
          handleCollectionSelect={this.handleNewCollection}
          smartCollectionsRestrict={false}
          showSmartSection={false}
        />
      </FormGroup>
    );
  }
}

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  customFields: state.Metadata.customFields,
  fileStatuses: state.Files.statuses,
  modalMode: state.Files.uploadModalMode,
  collection: state.Collections.collection,
  collectionUuid: state.Files.collectionUuid,
});

const mapDispatchToProps = dispatch => ({
  openCollectionSelectModal: (params) => {
    dispatch(showModal('CollectionSelectModal', params));
  },
});

UploadFileCollection.propTypes = {
  intl: PropTypes.object.isRequired,
  modalMode: PropTypes.string.isRequired,
  collection: PropTypes.object,
  collectionUuid: PropTypes.string,
  selectedFiles: PropTypes.array.isRequired,
  collectionError: PropTypes.bool.isRequired,
  isLinkEdit: PropTypes.bool.isRequired,
  handleCollectionChange: PropTypes.func.isRequired,
  existingFiles: PropTypes.object,
  allowCollectionSelect: PropTypes.bool,
};

UploadFileCollection.defaultProps = {
  collection: null,
  collectionUuid: null,
  allowCollectionSelect: true,
  existingFiles: {},
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UploadFileCollection));
